import { FC, useMemo } from "react";
import { Popover } from "@jugl-web/ui-components/cross-platform/Popover";
import { ListBox } from "@jugl-web/ui-components/cross-platform/ListBox";
import { useTranslations } from "@jugl-web/utils";
import { WhatsappVariableDefinition } from "@jugl-web/domain-resources/integrations/types/WhatsappVariableDefinition";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as OrderExternalLinkIcon } from "./assets/orderExternalLink.svg";
import { ReactComponent as TaskNameIcon } from "./assets/taskName.svg";
import { ReactComponent as DueDateIcon } from "./assets/dueDate.svg";
import { ReactComponent as LabelIcon } from "./assets/label.svg";
import { ReactComponent as StatusIcon } from "./assets/status.svg";
import { ReactComponent as PriorityIcon } from "./assets/priority.svg";
import { ReactComponent as CustomDropdownFieldIcon } from "./assets/customDropdownField.svg";
import { ReactComponent as CustomTextFieldIcon } from "./assets/customTextField.svg";
import { ReactComponent as CustomNumberFieldIcon } from "./assets/customNumberField.svg";
import { ReactComponent as CustomDateFieldIcon } from "./assets/customDateField.svg";
import { ReactComponent as CustomerNameIcon } from "./assets/customerName.svg";
import { ReactComponent as OrderNumberIcon } from "./assets/orderNumber.svg";
import { ToolbarButton } from "../ToolbarButton";

interface VariablesPopoverProps {
  variables: WhatsappVariableDefinition[];
  onVariableSelect: (variableId: string) => void;
}

const getVariableIcon = (
  label: string,
  type: WhatsappVariableDefinition["type"]
) => {
  if (type === "default") {
    const labelIconMap: Record<string, FC> = {
      "Customer Name": CustomerNameIcon,
      "Order Number": OrderNumberIcon,
      "Order External Link": OrderExternalLinkIcon,
      "Task Name": TaskNameIcon,
      "Due Date": DueDateIcon,
      Label: LabelIcon,
      Status: StatusIcon,
      Priority: PriorityIcon,
    };

    return labelIconMap[label] || CustomTextFieldIcon;
  }

  const typeIconMap: Record<
    Exclude<WhatsappVariableDefinition["type"], "default">,
    FC
  > = {
    custom: CustomTextFieldIcon,
    text: CustomTextFieldIcon,
    email: CustomTextFieldIcon,
    phone: CustomTextFieldIcon,
    date: CustomDateFieldIcon,
    number: CustomNumberFieldIcon,
    dropdown: CustomDropdownFieldIcon,
  };

  return typeIconMap[type] || CustomTextFieldIcon;
};

export const VariablesPopover: FC<VariablesPopoverProps> = ({
  variables,
  onVariableSelect,
}) => {
  const { t } = useTranslations();

  const variableListItems = useMemo(
    () =>
      variables?.map((variable) => ({
        id: variable.label,
        value: {
          label: variable.label,
          icon: getVariableIcon(variable.label, variable.type),
        },
      })) ?? [],
    [variables]
  );

  return (
    <Popover
      placement="right-end"
      className="max-w-[300px] p-4"
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          ref={triggerRef}
          as={ToolbarButton}
          isActive={isOpen}
          className="w-max gap-1 px-2"
        >
          <span>
            {t({
              id: "whatsapp-page.add-variable",
              defaultMessage: "Add Variable",
            })}
          </span>
          <PlusIcon />
        </Trigger>
      )}
    >
      {({ onClose }) => (
        <ListBox
          items={variableListItems}
          selectionBehavior={{ mode: "single", canToggle: false }}
          hasSearch
          hasSearchAutoFocus
          emptyStateMessage={t({
            id: "whatsapp-page.no-variables-available",
            defaultMessage: "No variables available",
          })}
          noSearchResultsMessage={t({
            id: "whatsapp-page.no-variables-found",
            defaultMessage: "No variables found",
          })}
          maxVisibleItems={6}
          itemSize="md"
          spaceBetweenItems="compact"
          renderLabel={(item) => item.value.label}
          renderStartIcon={(item) => {
            const IconComponent = item.value.icon;
            return <IconComponent />;
          }}
          renderSearchLabel={(item) => item.value.label}
          onSelect={(item) => {
            onVariableSelect(item.id);
            onClose();
          }}
        />
      )}
    </Popover>
  );
};
